import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { ComponentSync, LL_H_C, LL_V_L, ModuleFE_Thunderstorm, TS_Checkbox, TS_DropDown, TS_PropRenderer, TS_Tabs } from '@nu-art/thunderstorm-frontend/index';
import { __stringify, filterInstances } from '@nu-art/ts-common';
import { allowedParamTypes_NoteTemplate, allowedSubTypes_NoteTemplate, default_NoteTemplateSection } from '@app/advisor-content-shared';
import './Editor_NoteTemplate.scss';
import { Component_EditorHeader, FadeScrollWrapper, IconKeysAdapter, ICONS, viewMode_Edit, viewMode_View } from '@app/styles';
import { SearchableItem_NoteTemplate } from '../searchable-items.js';
import { Component_SwitchView } from '@nu-art/permissions-frontend/ui/Component_SwitchView';
import { DefaultEditor_InputText, DefaultEditor_InputText_Optional } from '@app/styles/frontend/ui-manager/ui-components';
import { extractParamKeysFromNoteTemplate, noteTemplateParamBuilder, translateNoteTemplateToNodes } from './utils.js';
import { Editor_SmartDocParams } from '../../../_enum/smart-doc-parameter/Editor_SmartDocParams/Editor_SmartDocParams.js';
import { Editor_NoteTemplateSections } from '../../../_enum/note-template-section/Editor_NoteTemplateSections/Editor_NoteTemplateSections.js';
import { Component_AdvisorNoteOption } from '../../components/advisor-note-option/Component_AdvisorNoteOption.js';
import { DropDown_FlowSheet } from '../../../_entity/flow-sheet/ui-components.js';
import { DropDown_DefaultCaret } from '@app/styles/frontend/ui/consts';
import { ModuleFE_AIPrompt, MultiSelect_AIPrompt } from '@nu-art/prompting-frontend';
import { AIPromptType } from '@nu-art/prompting-shared';
const filterPromptType = (type) => (id) => ModuleFE_AIPrompt.cache.unique(id)?.type === type;
export class Editor_NoteTemplate extends ComponentSync {
    // ######################## Lifecycle ########################
    deriveStateFromProps(nextProps, state) {
        state.editable = nextProps.editable;
        this.setEditableProps(state.editable);
        state.editMode = nextProps.editMode ?? (!state.editable.item._id ? true : (this.state?.editMode ?? false));
        return state;
    }
    // ######################## Logic ########################
    setEditableProps(editable) {
        editable.setPreSaveAction(template => {
            const paramKeys = extractParamKeysFromNoteTemplate(template);
            template.params = noteTemplateParamBuilder(paramKeys, template.params ?? []);
        });
        editable.setOnChanged(async () => this.forceUpdate());
    }
    getViewMode = () => this.state.editMode ? viewMode_Edit : viewMode_View;
    getItemActions = () => {
        return [{
                label: 'Edit Note Template',
                action: async () => this.setState({ editMode: true }),
            }, {
                label: 'Delete Note Template',
                action: async () => {
                    await this.state.editable.delete();
                    this.props.deselect?.();
                }
            }, {
                label: 'Export JSON',
                action: async () => {
                    const item = this.state.editable.item;
                    ModuleFE_Thunderstorm.downloadFile({
                        fileName: `Note Template - ${item.title}`,
                        mimeType: 'application/json',
                        content: __stringify(item, true)
                    });
                }
            }];
    };
    getEditModeActions = () => {
        //Actions for existing item
        if (this.state.editable.item._id)
            return [{
                    label: 'Done',
                    action: async () => this.setState({ editMode: false }),
                }];
        //Actions for new item
        return [{
                label: 'Cancel',
                action: async () => {
                    return this.props.deselect();
                },
            }, {
                label: 'Save',
                action: async () => {
                    await this.state.editable.save();
                },
            }];
    };
    getPromptPreviewText = (ids, type) => {
        if (!ids?.length)
            return '';
        const prompts = filterInstances(ids.filter(filterPromptType(type)).map(ModuleFE_AIPrompt.cache.unique));
        return prompts.map(p => p.content).join('\n\n');
    };
    // ######################## Render ########################
    render() {
        return _jsxs(LL_V_L, { id: 'editor__note-template', children: [!this.props.hideHeader && this.renderHeader(), this.renderBody()] });
    }
    renderHeader = () => {
        return _jsx(Component_EditorHeader, { editable: this.state.editable, searchItem: SearchableItem_NoteTemplate, editMode: this.state.editMode, itemActions: this.getItemActions(), editModeActions: this.getEditModeActions() });
    };
    renderBody = () => {
        const tabs = [
            {
                uid: 'general',
                title: 'General',
                content: this.render_Tab_General,
            },
            {
                uid: 'params',
                title: this.render_TabHeader_Params,
                content: this.render_Tab_Params,
            },
            {
                uid: 'advisor-appearance',
                title: 'Advisor Appearance',
                content: this.render_Tab_AdvisorAppearance,
            },
            {
                uid: 'prompts',
                title: 'Prompts',
                content: this.render_Tab_Prompts,
            }
        ];
        return _jsx(TS_Tabs, { tabs: tabs, className: 'editor__body' });
    };
    // ######################## Render - General Tab ########################
    render_Tab_General = () => {
        return _jsxs(LL_H_C, { className: 'editor__tab__general', children: [_jsxs(LL_V_L, { className: 'editor__column', children: [_jsxs(LL_H_C, { className: 'editor__row', children: [this.renderEditor_Label(), this.renderEditor_Title(), this.renderEditor_Flowsheet()] }), this.renderEditor_Sections()] }), this.render_Preview()] });
    };
    // ######################## Render - Params Tab ########################
    render_Tab_Params = () => {
        const params = this.state.editable.editProp('params', []);
        return _jsx(Editor_SmartDocParams, { editable: params, editMode: this.state.editMode, allowedParamTypes: allowedParamTypes_NoteTemplate, allowedParamSubTypes: allowedSubTypes_NoteTemplate });
    };
    render_TabHeader_Params = () => {
        const paramErrors = this.state.editable.hasError('params');
        return _jsxs(_Fragment, { children: ["Params ", paramErrors && _jsx(ICONS.attention.component, {})] });
    };
    // ######################## Render - Advisor Appearance Tab ########################
    render_Tab_AdvisorAppearance = () => {
        const editable = this.state.editable.editProp('advisorAppearance', {});
        return _jsxs(LL_H_C, { className: 'editor__tab__advisor-appearance', children: [_jsxs(LL_V_L, { className: 'editor__column', children: [this.render_Advisor_Label(editable), this.render_Advisor_Description(editable), this.render_Advisor_Icon(editable), this.render_Advisor_Dispositionable(editable)] }), _jsxs(LL_V_L, { className: 'editor__column', children: [_jsx(Component_AdvisorNoteOption, { note: this.state.editable.item, selected: false }), _jsx(Component_AdvisorNoteOption, { note: this.state.editable.item, selected: true })] })] });
    };
    render_Advisor_Label = (editable) => {
        return _jsx(TS_PropRenderer.Vertical, { label: 'Title', children: _jsx(DefaultEditor_InputText_Optional, { editable: editable, prop: 'title' }) });
    };
    render_Advisor_Description = (editable) => {
        return _jsx(TS_PropRenderer.Vertical, { label: 'Description', children: _jsx(DefaultEditor_InputText_Optional, { editable: editable, prop: 'description' }) });
    };
    render_Advisor_Icon = (editable) => {
        return _jsx(TS_PropRenderer.Vertical, { label: 'Icon', children: _jsx(TS_DropDown, { canUnselect: true, adapter: IconKeysAdapter, onSelected: key => editable.updateObj({ iconKey: key }), selected: editable.item.iconKey }) });
    };
    render_Advisor_Dispositionable = (editable) => {
        return _jsx(TS_PropRenderer.Horizontal, { label: 'Dispositionable', onClick: () => {
                if (!this.state.editMode)
                    return;
                editable.updateObj({ dispositionable: !editable.item.dispositionable });
            }, children: _jsx(TS_Checkbox, { checked: editable.item.dispositionable }) });
    };
    // ######################## Render - Editors ########################
    renderEditor_Label = () => {
        const editable = this.state.editable;
        return _jsx(TS_PropRenderer.Vertical, { label: 'Label', children: _jsx(Component_SwitchView, { mode: this.getViewMode(), modes: [
                    { key: viewMode_View, renderer: () => _jsx("div", { className: 'editor__value', children: editable.item.label }) },
                    { key: viewMode_Edit, renderer: () => _jsx(DefaultEditor_InputText, { editable: editable, prop: 'label' }) }
                ] }) });
    };
    renderEditor_Title = () => {
        const editable = this.state.editable;
        return _jsx(TS_PropRenderer.Vertical, { label: 'Title', children: _jsx(Component_SwitchView, { mode: this.getViewMode(), modes: [
                    { key: viewMode_View, renderer: () => _jsx("div", { className: 'editor__value', children: editable.item.title }) },
                    { key: viewMode_Edit, renderer: () => _jsx(DefaultEditor_InputText, { editable: editable, prop: 'title' }) }
                ] }) });
    };
    renderEditor_Flowsheet = () => {
        const editable = this.state.editable;
        return _jsx(TS_PropRenderer.Vertical, { label: 'Smart Link', children: _jsx(DropDown_FlowSheet.editable, { editable: editable, prop: 'flowSheetId', placeholder: 'Choose a Smart Link', disabled: this.getViewMode() !== viewMode_Edit, canUnselect: true, caret: this.getViewMode() !== viewMode_Edit ? undefined : DropDown_DefaultCaret }) });
    };
    renderEditor_Sections = () => {
        const sections = this.state.editable.editProp('sections', []);
        const label = () => _jsxs(_Fragment, { children: ["Sections", this.state.editMode && _jsx(ICONS.advisor_avn_add.component, { onClick: () => sections.updateArrayAt(default_NoteTemplateSection) })] });
        return _jsx(TS_PropRenderer.Vertical, { label: label(), className: 'editor__note-template__sections', children: _jsx(FadeScrollWrapper, { children: _jsx(Editor_NoteTemplateSections, { editable: sections, editMode: this.state.editMode, indent: false, level: 1 }) }) });
    };
    render_Preview = () => {
        const template = this.state.editable.item;
        return _jsx(TS_PropRenderer.Vertical, { label: 'Preview', className: 'editor__note-template__preview', children: _jsx("p", { children: translateNoteTemplateToNodes(template) }) });
    };
    // ######################## Render - Prompts Tab ########################
    render_Tab_Prompts = () => {
        const currentIds = this.state.editable.get('promptIds');
        return _jsxs(LL_H_C, { className: 'editor__tab__prompts', children: [_jsxs(LL_V_L, { className: 'editor__column', children: [_jsx(TS_PropRenderer.Vertical, { label: 'User Prompts', children: _jsx(MultiSelect_AIPrompt, { editable: this.state.editable, prop: 'promptIds', selectionFilter: filterPromptType(AIPromptType.user), itemFilter: filterPromptType(AIPromptType.user), disabled: !this.state.editMode }) }), _jsx(TS_PropRenderer.Vertical, { label: 'System Prompts', children: _jsx(MultiSelect_AIPrompt, { editable: this.state.editable, prop: 'promptIds', selectionFilter: filterPromptType(AIPromptType.system), itemFilter: filterPromptType(AIPromptType.system), disabled: !this.state.editMode }) })] }), _jsx(LL_V_L, { className: 'editor__column', children: _jsx(TS_PropRenderer.Vertical, { label: 'User Prompt - Preview', className: 'prompt-preview', children: _jsx("div", { className: 'prompt-preview-text', children: this.getPromptPreviewText(currentIds, AIPromptType.user) }) }) }), _jsx(LL_V_L, { className: 'editor__column', children: _jsx(TS_PropRenderer.Vertical, { label: 'System Prompt - Preview', className: 'prompt-preview', children: _jsx("div", { className: 'prompt-preview-text', children: this.getPromptPreviewText(currentIds, AIPromptType.system) }) }) })] });
    };
}
//# sourceMappingURL=Editor_NoteTemplate.js.map